import {useCallback, useEffect, useMemo, useState} from 'react';
import {applyEdgeChanges, applyNodeChanges, Edge, EdgeChange, Node, NodeChange, OnSelectionChangeParams} from 'reactflow';
import {useSelector} from 'react-redux';
import {IAppliedRule, RuleResultType} from '../../../../../incase-domain/appliedRules';
import {
    selectedPresentationSlidesSelector,
    selectedPresentationSlidePreviewsSelector,
} from '../../../../../store/selectors/presentation.selectors';
import {ActionNodeData, RULE_TYPE_MODIFIER} from '../nodes.types';
import {ghostEdgeStyleForModifier, ruleEdgeStyleForModifier} from '../../util/flow.styles';
import {buildStructuralEdges, buildStructuralNodes} from '../../util/flowStructure';
import {rulesToFlowElements} from '../../util/rulesToFlowElements';

export interface UseFlowStateParams {
    initialRules: IAppliedRule[];
    isOpen: boolean;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useFlowState({initialRules, isOpen}: UseFlowStateParams) {
    const slides = useSelector(selectedPresentationSlidesSelector);
    const previews = useSelector(selectedPresentationSlidePreviewsSelector);

    const [nodes, setNodes] = useState<Node[]>(() => buildStructuralNodes(slides, previews));
    const [edges, setEdges] = useState<Edge[]>(() => buildStructuralEdges(slides));
    const [selectedActionIds, setSelectedActionIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        const freshStructural = buildStructuralNodes(slides, previews);
        setNodes((prev) => [...freshStructural, ...prev.filter((n) => n.id.startsWith('action-'))]);
    }, [slides, previews]);

    useEffect(() => {
        const freshStructural = buildStructuralEdges(slides);
        setEdges((prev) => [...freshStructural, ...prev.filter((e) => e.id.startsWith('pred-'))]);
    }, [slides]);

    const makeCallbacks = useCallback(
        (nodeId: string) => ({
            onChangeType: (newType: RuleResultType) => {
                const modifier = RULE_TYPE_MODIFIER[newType] ?? 'jump';
                setNodes((ns) => ns.map((n) => (n.id === nodeId ? {...n, data: {...n.data, ruleType: newType}} : n)));
                setEdges((es) => es.map((e) => (e.target === nodeId ? {...e, ...ruleEdgeStyleForModifier(modifier)} : e)));
            },
            onAddSlide: (slideId: string) =>
                setNodes((ns) =>
                    ns.map((n) =>
                        n.id === nodeId
                            ? {
                                  ...n,
                                  data: {
                                      ...n.data,
                                      slideIds: n.data.slideIds.includes(slideId) ? n.data.slideIds : [...n.data.slideIds, slideId],
                                  },
                              }
                            : n
                    )
                ),
            onRemoveSlide: (slideId: string) =>
                setNodes((ns) =>
                    ns.map((n) =>
                        n.id === nodeId ? {...n, data: {...n.data, slideIds: n.data.slideIds.filter((s: string) => s !== slideId)}} : n
                    )
                ),
        }),
        []
    );

    useEffect(() => {
        if (!isOpen) return;
        const {actionNodes, predicateEdges} = rulesToFlowElements(initialRules, slides, makeCallbacks);
        setNodes((prev) => [...prev.filter((n) => !n.id.startsWith('action-')), ...actionNodes]);
        setEdges((prev) => [...prev.filter((e) => !e.id.startsWith('pred-')), ...predicateEdges]);
    }, [initialRules, isOpen, makeCallbacks, slides]);

    const ghostEdges = useMemo(
        () =>
            nodes
                .filter((n) => n.id.startsWith('action-'))
                .flatMap((n) => {
                    const modifier = RULE_TYPE_MODIFIER[(n.data as ActionNodeData).ruleType] ?? 'jump';
                    const isSelected = selectedActionIds.has(n.id);
                    return ((n.data as ActionNodeData).slideIds ?? []).map((slideId) => ({
                        id: `ghost-${n.id}-${slideId}`,
                        source: n.id,
                        sourceHandle: 'action-ghost-out',
                        target: `slide-${slideId}`,
                        targetHandle: 'incoming',
                        ...ghostEdgeStyleForModifier(modifier),
                        selected: isSelected,
                    }));
                }),
        [nodes, selectedActionIds]
    );

    const allEdges = useMemo(
        () => [...edges.map((e) => ({...e, selected: selectedActionIds.has(e.target) ? true : e.selected})), ...ghostEdges],
        [edges, ghostEdges, selectedActionIds]
    );

    const onSelectionChange = useCallback(({nodes: selectedNodes}: OnSelectionChangeParams) => {
        setSelectedActionIds(new Set(selectedNodes.filter((n) => n.id.startsWith('action-')).map((n) => n.id)));
    }, []);

    const onNodesChange = useCallback((changes: NodeChange[]) => {
        setNodes((ns) => applyNodeChanges(changes, ns));
    }, []);

    const onEdgesChange = useCallback((changes: EdgeChange[]) => {
        const nonGhost = changes.filter((c) => !('id' in c && (c.id as string).startsWith('ghost-')));
        setEdges((es) => applyEdgeChanges(nonGhost, es));
    }, []);

    return {nodes, setNodes, edges, setEdges, allEdges, slides, makeCallbacks, onSelectionChange, onNodesChange, onEdgesChange};
}
